'use client';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../assets/icons/Icon';
import { useApiClient } from '../../api/ApiClientContext';
import type { LeaderboardMemberResponse } from '../../api/Api';
import styles from './LeaderboardEndorsementModal.module.scss';

interface EndorsementOptionItem {
  id: string;
  label: string;
}

const HABIT_ICONS: Array<'OneIcon' | 'TwoIcon' | 'ThreeIcon' | 'FourIcon'> = [
  'OneIcon',
  'TwoIcon',
  'ThreeIcon',
  'FourIcon',
];

function getHabitIcon(habitId: string, index: number) {
  const hash = habitId
    ? habitId.charCodeAt(0) + habitId.charCodeAt(habitId.length - 1)
    : index;
  return HABIT_ICONS[hash % HABIT_ICONS.length];
}

interface LeaderboardEndorsementModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: LeaderboardMemberResponse;
  onEndorsed: () => void;
}

export const LeaderboardEndorsementModal: React.FC<LeaderboardEndorsementModalProps> = ({
  isOpen,
  onClose,
  member,
  onEndorsed,
}) => {
  const apiClient = useApiClient();
  const [step, setStep] = useState<'habits' | 'endorse'>('habits');
  const [selectedHabit, setSelectedHabit] = useState<LeaderboardMemberResponse['habits'][number] | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: endorsementOptions = [] } = useQuery<EndorsementOptionItem[]>({
    queryKey: ['endorsementOptions'],
    queryFn: async () => {
      const res = await apiClient.api.getEndorsementOptionsApiV1SocialEndorsementOptionsGet();
      return res?.data;
    },
    staleTime: Infinity,
  });

  const handleEndorseClick = (habit: LeaderboardMemberResponse['habits'][number]) => {
    setSelectedHabit(habit);
    setSelectedOptionId('');
    setError(null);
    setStep('endorse');
  };

  const handleBack = () => {
    setStep('habits');
    setSelectedHabit(null);
    setSelectedOptionId('');
    setIsDropdownOpen(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedHabit || !selectedOptionId) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await apiClient.api.createEndorsementApiV1SocialEndorsementsPost({
        endorsed_user_id: member.user.id,
        habit_id: selectedHabit.habit_id,
        endorsement_option: selectedOptionId,
      });
      onEndorsed();
      handleClose();
    } catch (err: any) {
      const msg = err?.error?.detail || err?.message || 'Failed to endorse';
      setError(typeof msg === 'string' ? msg : 'Failed to endorse');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setStep('habits');
    setSelectedHabit(null);
    setSelectedOptionId('');
    setIsDropdownOpen(false);
    setError(null);
  };

  const getInitials = () => {
    const first = member.user.first_name?.[0] || '';
    const last = member.user.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const selectedOptionLabel = endorsementOptions.find(
    (o) => o.id === selectedOptionId
  )?.label;

  const unendorsedHabits = member.habits.filter((h) => !h.endorsed_this_habit_today);
  const allEndorsed = unendorsedHabits.length === 0;

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Endorse your teammate</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <Icon iconName="CrossIcon" width="1.25rem" height="1.25rem" />
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              {member.user.avatar_url ? (
                <img src={member.user.avatar_url} alt={member.user.full_name} />
              ) : (
                <span>{getInitials()}</span>
              )}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>{member.user.full_name}</span>
              {member.user.job_title && (
                <span className={styles.userJobTitle}>{member.user.job_title}</span>
              )}
            </div>
          </div>

          {step === 'habits' && (
            <div className={styles.userSubtitle}>
              {allEndorsed
                ? 'All habits already endorsed today'
                : 'Developing following habits:'}
            </div>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}

          {step === 'habits' ? (
            <div className={styles.habitsList}>
              {member.habits.map((habit, index) => {
                const alreadyEndorsed = habit.endorsed_this_habit_today;
                return (
                  <div
                    key={habit.habit_id}
                    className={`${styles.habitRow} ${alreadyEndorsed ? styles.habitRowDisabled : ''}`}
                  >
                    <div className={styles.habitIcon}>
                      <Icon
                        iconName={getHabitIcon(habit.habit_id, index)}
                        width="2.25rem"
                        height="2.25rem"
                      />
                    </div>
                    <span className={styles.habitName}>{habit.habit_name}</span>
                    {alreadyEndorsed ? (
                      <span className={styles.endorsedLabel}>Endorsed</span>
                    ) : (
                      <button
                        className={styles.endorseBtn}
                        onClick={() => handleEndorseClick(habit)}
                      >
                        Endorse
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.endorseStep}>
              <button className={styles.backButton} onClick={handleBack}>
                <span className={styles.backArrow}>
                  <Icon iconName="ArrowRightIcon" width="1rem" height="1rem" />
                </span>
                Back to habits
              </button>
              <div className={styles.selectedHabit}>
                <div className={styles.habitIcon}>
                  <Icon
                    iconName={getHabitIcon(
                      selectedHabit?.habit_id || '',
                      member.habits.indexOf(selectedHabit!)
                    )}
                    width="2.25rem"
                    height="2.25rem"
                  />
                </div>
                <span className={styles.habitName}>
                  {selectedHabit?.habit_name}
                </span>
              </div>

              <p className={styles.question}>
                Have you seen your team member practise this habit?
              </p>

              <div className={styles.dropdownWrapper}>
                <label className={styles.dropdownLabel}>Endorsement option</label>
                <div
                  className={`${styles.dropdown} ${isDropdownOpen ? styles.dropdownOpen : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span
                    className={
                      selectedOptionLabel
                        ? styles.dropdownValue
                        : styles.dropdownPlaceholder
                    }
                  >
                    {selectedOptionLabel || 'Select'}
                  </span>
                  <Icon
                    iconName={isDropdownOpen ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                    width="1rem"
                    height="1rem"
                  />
                </div>

                {isDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    {endorsementOptions.map((option) => (
                      <div
                        key={option.id}
                        className={`${styles.dropdownItem} ${selectedOptionId === option.id ? styles.dropdownItemSelected : ''}`}
                        onClick={() => {
                          setSelectedOptionId(option.id);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.actions}>
                <button
                  className={styles.saveBtn}
                  onClick={handleSave}
                  disabled={!selectedOptionId || isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
